import { Grid } from '@mui/material'
import {
  BorderlessAccordion,
  CellProps,
  createKeysAndColumnNameArray,
  DenseTable,
  getDenseTableRowProps,
  NotaBene,
  PageLayoutWithTitleAndInfo,
  RedirectButton,
  TableType,
  TitledSection
} from '../../components'
import { PaymentHistory, PaymentInfo } from './components'
import makeStyles from '@mui/styles/makeStyles'
import { useStateWrapper } from '../../hooks'
import { GET_MEMBERSHIP_FEES } from '../../graphql'
import { MembershipFeesQueryType } from '../../types/types'
import {
  ROUTE_ADD_PAYMENT_EXEMPTION,
  ROUTE_CALCULATE_MEMBERSHIP_FEE
} from '../../routes/constants/routeConstants'
import {
  RefenceNumber,
  SelfPaymentObligation
} from '../../utils/classes/graphql'
import { BASIC_DATE_FORMAT } from '../../utils'
import { useIsMobile } from '../../contexts'

const useStyles = makeStyles((theme) => ({
  somePaddings: {
    paddingTop: '1.2rem',
    [theme.breakpoints.up('md')]: {
      paddingTop: '1rem'
    }
  },
  hiddenTable: {
    display: 'none'
  }
}))

interface CopiableRefenceNumber extends RefenceNumber {
  copy?: string | undefined
}

const MembershipFeesData = () => {
  const { StateRenderWrapper, data: membershipFees } =
    useStateWrapper<MembershipFeesQueryType>({
      query: GET_MEMBERSHIP_FEES
    })

  const classes = useStyles()

  const referenceNumberTypes: CellProps<CopiableRefenceNumber>[] =
    createKeysAndColumnNameArray('pages.membershipFeesPage.referenceNumbers.', [
      {
        key: 'alkupvm',
        translationIdPostfix: 'periodStartDate',
        dateFormat: BASIC_DATE_FORMAT
      },
      {
        key: 'loppupvm',
        translationIdPostfix: 'periodEndDate',
        dateFormat: BASIC_DATE_FORMAT
      },
      {
        key: 'viitenumero',
        translationIdPostfix: 'referenceNumber'
      },
      {
        key: 'erapaiva',
        translationIdPostfix: 'dueDate',
        dateFormat: BASIC_DATE_FORMAT
      },
      {
        key: 'pankkiviivakoodi',
        translationIdPostfix: 'virtualBarcode'
      },
      {
        key: 'copy',
        translationIdPostfix: 'copy',
        keyToCopy: 'pankkiviivakoodi'
      }
    ])

  /*
     tunniste
     maksurytmi
     aloituspvm
     lopetuspvm
     viitelajiKoodi
     viitelaji
     maksujaksonPituus
     elaskuSumma
     viitetulostus  
    */

  const selfPaymentObligationsTypes: CellProps<SelfPaymentObligation>[] =
    createKeysAndColumnNameArray(
      'pages.membershipFeesPage.selfPaymentObligations.',
      [
        {
          key: 'viitelaji',
          translationIdPostfix: 'referenceType'
        },
        {
          key: 'maksujaksonPituus',
          translationIdPostfix: 'paymentPeriodLength',
          displayWhenNull: '-'
        },
        {
          key: 'aloituspvm',
          translationIdPostfix: 'startDate',
          dateFormat: BASIC_DATE_FORMAT,
          displayWhenNull: '-'
        },
        {
          key: 'lopetuspvm',
          translationIdPostfix: 'endDate',
          dateFormat: BASIC_DATE_FORMAT,
          displayWhenNull: '-'
        }
      ]
    )

  return (
    <StateRenderWrapper>
      <TitledSection translationid="pages.membershipFeesPage.paymentInfoSectionTitle">
        {membershipFees && <PaymentInfo membershipFees={membershipFees} />}
        <NotaBene translationid="pages.membershipFeesPage.importantNotice" />
        <BorderlessAccordion
          titleTranslationId="pages.membershipFeesPage.referenceNumbers.title"
          defaultExpanded
        >
          <DenseTable
            type={TableType.Static}
            cellProps={referenceNumberTypes}
            data={getDenseTableRowProps(membershipFees?.refenceNumbers)}
            tableStylingProps={{ addBorderToFirstMobileColumnBox: true }}
          />
        </BorderlessAccordion>
        <DenseTable
          type={TableType.Static}
          // Hidden on request, if returned change classes from hidden to somePaddings
          className={classes.hiddenTable}
          cellProps={selfPaymentObligationsTypes}
          data={getDenseTableRowProps(
            membershipFees?.membershipFeeObligations?.selfPaymentObligations
          )}
          tableStylingProps={{ addBorderToFirstMobileColumnBox: true }}
        />
      </TitledSection>
      <TitledSection translationid="pages.membershipFeesPage.paymentHistorySectionTitle">
        {membershipFees && <PaymentHistory membershipFees={membershipFees} />}
      </TitledSection>
    </StateRenderWrapper>
  )
}

export const MembershipFees = () => {
  const isMobile = useIsMobile()

  return (
    <PageLayoutWithTitleAndInfo
      infoBoxProps={{ translationId: 'pages.membershipFeesPage.infoBoxText' }}
      topElement={
        <Grid
          container
          justifyContent={isMobile ? 'flex-start' : 'flex-end'}
          columnSpacing={{ md: 2 }}
          rowSpacing={{ xs: 2 }}
          mb={2}
        >
          <Grid item>
            <RedirectButton
              redirectPath={ROUTE_CALCULATE_MEMBERSHIP_FEE}
              translationId="pages.membershipFeesPage.button.calculateMembershipFee"
            />
          </Grid>
          <Grid item>
            <RedirectButton
              redirectPath={ROUTE_ADD_PAYMENT_EXEMPTION}
              translationId="pages.membershipFeesPage.button.reportPaymentExemption"
            />
          </Grid>
        </Grid>
      }
    >
      <MembershipFeesData />
    </PageLayoutWithTitleAndInfo>
  )
}
